import { Box, DialogActions as MuiDialogActions } from '@mui/material';
import { LoadingButton, LoadingButtonProps } from '@mui/lab';
import { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import UtmsButton from '@shared/components/ui/UtmsButton';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { utmsButtonSx } from '@shared/constants/otherConstants';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

interface BaseButtonConfig {
    disabled?: boolean;
    color?: LoadingButtonProps['color'];
    text?: string;
}

interface LoadingButtonConfig extends BaseButtonConfig {
    loading?: boolean;
    endIcon?: ReactNode;
    showEndIcon?: boolean;
}

interface BackButtonConfig extends BaseButtonConfig {
    show?: boolean;
    showOnlyOnLastStep?: boolean;
}

interface MultiStepDialogActionsProps {
    isFirstStep: boolean;
    isLastStep: boolean;
    onNext: () => void;
    onBack: () => void;
    onCancel: () => void;
    nextButtonConfig?: LoadingButtonConfig;
    backButtonConfig?: BackButtonConfig;
    beforeButtons?: ReactNode;
    useDialogActions?: boolean;
}

const MultiStepDialogActions: FC<MultiStepDialogActionsProps> = ({
    isFirstStep,
    isLastStep,
    onNext,
    onBack,
    onCancel,
    nextButtonConfig = {},
    backButtonConfig = {},
    beforeButtons,
    useDialogActions = false,
}) => {
    const { t } = useTranslation();

    const defaultNextText = isLastStep ? t('run') : t('next');
    const { show = true, showOnlyOnLastStep = false } = backButtonConfig;
    const shouldShowBackButton = show && !isFirstStep && (!showOnlyOnLastStep || isLastStep);

    const getNextEndIcon = () => {
        if (nextButtonConfig.endIcon !== undefined) {
            return nextButtonConfig.endIcon;
        }
        if (isLastStep) {
            return (
                <PlayArrowIcon
                    sx={{ display: nextButtonConfig.loading ? 'none' : 'flex' }}
                    color={nextButtonConfig.disabled ? 'disabled' : nextButtonConfig.color}
                />
            );
        }
        if (!nextButtonConfig.showEndIcon) {
            return;
        }
        return <ChevronRight color={nextButtonConfig.disabled ? 'disabled' : 'primary'} />;
    };

    const buttonsContent = (
        <>
            {shouldShowBackButton && (
                <UtmsButton
                    onClick={onBack}
                    color={backButtonConfig.color ?? 'secondary'}
                    disabled={backButtonConfig.disabled}
                    sx={{ mr: 1 }}
                    startIcon={<ChevronLeft />}
                >
                    {backButtonConfig.text ?? t('back')}
                </UtmsButton>
            )}
            <LoadingButton
                sx={utmsButtonSx}
                disabled={nextButtonConfig.disabled}
                loading={nextButtonConfig.loading}
                color={nextButtonConfig.color ?? 'primary'}
                onClick={onNext}
                endIcon={getNextEndIcon()}
            >
                {nextButtonConfig.text ?? defaultNextText}
            </LoadingButton>
            <UtmsButton onClick={onCancel} color="secondary" sx={{ mr: 1 }}>
                {t('cancel')}
            </UtmsButton>
        </>
    );

    if (useDialogActions) {
        return (
            <MuiDialogActions>
                {beforeButtons}
                {buttonsContent}
            </MuiDialogActions>
        );
    }

    return (
        <Box
            sx={{
                display: 'flex',
                pt: 2,
                mt: 'auto',
                justifyContent: 'flex-end',
            }}
        >
            {buttonsContent}
        </Box>
    );
};

export default MultiStepDialogActions;
